/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { T as TinyEmitter } from './vueprint.b71ecad4.mjs';
import { ref, computed } from 'vue';
import { g as getDebugger } from './vueprint.b84f56b4.mjs';

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _uuid, _channel, _localBus, _crossTabBus, _requestResponseBus, _requestResponseHandlers, _active, _lastUpdatedAt, _alreadyTriggeredLocalEvents, _alreadyTriggeredCrossTabEvents, _onChannelMessage, onChannelMessage_fn, _onWindowFocusChange, onWindowFocusChange_fn, _onWindowVisibilitychange, onWindowVisibilitychange_fn, _awaitLocal, awaitLocal_fn, _awaitCrossTab, awaitCrossTab_fn, _addRequestHandler, addRequestHandler_fn;
const debug = getDebugger("Bus");
function shortid() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}
class BusService {
  /**
   * Create a new bus
   * @param namespace The namespace for the BroadcastChannel
   */
  constructor(namespace) {
    __privateAdd(this, _onChannelMessage);
    __privateAdd(this, _onWindowFocusChange);
    __privateAdd(this, _onWindowVisibilitychange);
    __privateAdd(this, _awaitLocal);
    __privateAdd(this, _awaitCrossTab);
    __privateAdd(this, _addRequestHandler);
    __privateAdd(this, _uuid, void 0);
    __privateAdd(this, _channel, void 0);
    __privateAdd(this, _localBus, void 0);
    __privateAdd(this, _crossTabBus, void 0);
    __privateAdd(this, _requestResponseBus, void 0);
    __privateAdd(this, _requestResponseHandlers, void 0);
    __privateAdd(this, _active, void 0);
    __privateAdd(this, _lastUpdatedAt, void 0);
    __privateAdd(this, _alreadyTriggeredLocalEvents, void 0);
    __privateAdd(this, _alreadyTriggeredCrossTabEvents, void 0);
    __privateSet(this, _uuid, shortid());
    __privateSet(this, _localBus, new TinyEmitter());
    __privateSet(this, _crossTabBus, new TinyEmitter());
    __privateSet(this, _requestResponseBus, new TinyEmitter());
    __privateSet(this, _requestResponseHandlers, /* @__PURE__ */ new Map());
    __privateSet(this, _active, ref(false));
    __privateSet(this, _lastUpdatedAt, ref(void 0));
    __privateSet(this, _alreadyTriggeredLocalEvents, ref({}));
    __privateSet(this, _alreadyTriggeredCrossTabEvents, ref({}));
    const origin = "undefined" !== typeof window && "object" === typeof window.location && "string" === typeof window.location.origin ? window.location.origin : "vueprint";
    if (typeof BroadcastChannel !== "undefined") {
      __privateSet(this, _channel, new BroadcastChannel(namespace || origin || "vueprint"));
      __privateGet(this, _channel).onmessage = __privateMethod(this, _onChannelMessage, onChannelMessage_fn).bind(this);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", __privateMethod(this, _onWindowFocusChange, onWindowFocusChange_fn).bind(this, true));
      window.addEventListener("blur", __privateMethod(this, _onWindowFocusChange, onWindowFocusChange_fn).bind(this, false));
      window.addEventListener("visibilitychange", __privateMethod(this, _onWindowVisibilitychange, onWindowVisibilitychange_fn).bind(this));
      __privateMethod(this, _onWindowVisibilitychange, onWindowVisibilitychange_fn).call(this);
    }
    __privateMethod(this, _addRequestHandler, addRequestHandler_fn).call(this, "getActiveTabs", () => this.active.value);
    __privateMethod(this, _addRequestHandler, addRequestHandler_fn).call(this, "awaitCrossTab", async ({ event, args }) => {
      await __privateMethod(this, _awaitLocal, awaitLocal_fn).call(this, event, args);
    });
    debug(`Initialized Bus for tab ${__privateGet(this, _uuid)}`);
  }
  /**
   * The UUID of the tab
   */
  get uuid() {
    return __privateGet(this, _uuid);
  }
  /**
   * Whether the tab is active
   */
  get active() {
    return computed(() => __privateGet(this, _active).value);
  }
  /**
   * Whether the tab has been inactive for too long and should have reduced functionality
   * to save user resources
   */
  get inactiveTooLong() {
    return computed(() => {
      if (__privateGet(this, _active).value === true || __privateGet(this, _lastUpdatedAt).value === void 0) {
        return false;
      }
      return Date.now() - __privateGet(this, _lastUpdatedAt).value > 6e4;
    });
  }
  /**
   * Listen to an event
   * @param event The event to listen to
   * @param callback The callback to call when the event is emitted
   * @param options The options for listening to the event
   */
  on(event, callback, options = {}) {
    if (options.local) {
      __privateGet(this, _localBus).on(event, callback);
    }
    if (options.crossTab) {
      __privateGet(this, _crossTabBus).on(event, callback);
    }
    if (options.immediate) {
      let called = false;
      const args = __privateGet(this, _alreadyTriggeredLocalEvents).value[event] ? Object.values(__privateGet(this, _alreadyTriggeredLocalEvents).value[event]) : [];
      if (options.local && __privateGet(this, _alreadyTriggeredLocalEvents).value[event]) {
        called = true;
        callback(...args);
      }
      if (options.crossTab && __privateGet(this, _alreadyTriggeredCrossTabEvents).value[event] && !called) {
        called = true;
        callback(...args);
      }
    }
  }
  /**
   * Stop listening to an event
   * @param event The event to stop listening to
   * @param callback The callback to remove from the event
   * @param options The options for stopping listening to the event
   */
  off(event, callback, options = {}) {
    if (options.local) {
      __privateGet(this, _localBus).off(event, callback);
    }
    if (options.crossTab) {
      __privateGet(this, _crossTabBus).off(event, callback);
    }
  }
  /**
   * Listen to an event once
   * @param event The event to listen to
   * @param callback The callback to call when the event is emitted
   * @param options The options for listening to the event
   */
  once(event, callback, options = {}) {
    if (options.local) {
      __privateGet(this, _localBus).once(event, callback);
    }
    if (options.crossTab) {
      __privateGet(this, _crossTabBus).once(event, callback);
    }
    if (options.immediate) {
      let called = false;
      const args = __privateGet(this, _alreadyTriggeredLocalEvents).value[event] ? Object.values(__privateGet(this, _alreadyTriggeredLocalEvents).value[event]) : [];
      if (options.local && __privateGet(this, _alreadyTriggeredLocalEvents).value[event]) {
        called = true;
        callback(...args);
      }
      if (options.crossTab && __privateGet(this, _alreadyTriggeredCrossTabEvents).value[event] && !called) {
        called = true;
        callback(...args);
      }
    }
  }
  /**
   * Trigger an event
   * @param event The name of the event to emit
   * @param options The options for emitting the event
   * @param args The arguments to pass to the event
   */
  emit(event, options = {}, ...args) {
    if (options.local) {
      __privateGet(this, _localBus).emit(event, ...args);
      __privateGet(this, _alreadyTriggeredLocalEvents).value[event] = args;
    }
    if (options.crossTab) {
      __privateGet(this, _channel)?.postMessage(JSON.stringify({ event, args, from: __privateGet(this, _uuid) }));
      __privateGet(this, _alreadyTriggeredCrossTabEvents).value[event] = args;
    }
  }
  /**
   * Trigger an event and await for all listeners to process it
   * @param event The name of the event to await listener processing for
   * @param args The arguments to pass to the event
   * @returns A promise that resolves when all listeners have processed the event
   *
   * @remarks
   * This method is especially useful within service workers where you may need to use `event.waitUntil` to ensure that all listeners have processed the event before the service worker is terminated
   */
  async await(event, options = {}, ...args) {
    const promises = [];
    if (options.local) {
      promises.push(__privateMethod(this, _awaitLocal, awaitLocal_fn).call(this, event, args));
    }
    if (options.crossTab) {
      promises.push(__privateMethod(this, _awaitCrossTab, awaitCrossTab_fn).call(this, event, args));
    }
    if (promises.length) {
      return await Promise.all(promises);
    }
    return await Promise.resolve();
  }
  /**
   * Make a request to all tabs and await their responses
   * @param method The method to call
   * @param payload The payload to send to the method being called
   * @param targets The uuids of the tabs to send the request to. Accepts "*" for all tabs
   * @param timeout The amount of time to wait for a response
   * @returns A map of responses from the tabs
   */
  async crossTabRequest(method, payload, targets = "*", timeout = 500) {
    if (!__privateGet(this, _channel)) {
      throw new Error("BroadcastChannel is not available");
    }
    const requestId = shortid();
    const requestPayload = JSON.stringify({
      event: "crossTabRequest",
      args: [requestId, method, payload, targets],
      from: __privateGet(this, _uuid)
    });
    const responses = /* @__PURE__ */ new Map();
    const onResponse = (response, from) => {
      responses.set(from, response);
    };
    const promiseForResponses = new Promise((resolve) => {
      __privateGet(this, _requestResponseBus).on(requestId, onResponse);
      setTimeout(resolve, timeout);
    });
    __privateGet(this, _channel).postMessage(requestPayload);
    await promiseForResponses;
    __privateGet(this, _requestResponseBus).off(requestId, onResponse);
    return responses;
  }
  /**
   * Add a function which will handle requests for a method called by {@link BusService.crossTabRequest}
   * @param method The method which the handler will handle requests for
   * @param handler The function which will handle requests and return results
   */
  addRequestHandler(method, handler) {
    const protectedMethods = ["getActiveTabs", "awaitCrossTab"];
    if (protectedMethods.includes(method)) {
      throw new Error(`Method "${method}" is protected and cannot be overridden`);
    }
    __privateMethod(this, _addRequestHandler, addRequestHandler_fn).call(this, method, handler);
  }
  /**
   * Get the active tabs
   * @param wait The time to wait before returning the active tabs
   * @returns The active tabs
   */
  async getActiveTabs(wait = 500) {
    const knownTabs = /* @__PURE__ */ new Map();
    const responses = await this.crossTabRequest("getActiveTabs", void 0, "*", wait);
    responses.forEach((response, from) => {
      knownTabs.set(from, response);
    });
    const tabs = Array.from(knownTabs.entries());
    tabs.push([__privateGet(this, _uuid), __privateGet(this, _active).value]);
    const sorted = tabs.sort((a, b) => {
      const [aUuid, aActive] = a;
      const [bUuid, bActive] = b;
      if (aActive === bActive) {
        return aUuid.localeCompare(bUuid, void 0, {
          numeric: false,
          sensitivity: "base",
          ignorePunctuation: true
        });
      }
      return aActive === true ? -1 : 1;
    });
    const ids = sorted.map((tab) => tab[0]);
    return ids;
  }
  /**
   * Check if the tab is the main tab
   * @param wait The time to wait before returning the active tabs
   * @returns Whether the tab is the main tab
   */
  async isMain(wait = 500) {
    const ids = await this.getActiveTabs(wait);
    return ids.length > 0 ? ids[0] === __privateGet(this, _uuid) : false;
  }
}
_uuid = new WeakMap();
_channel = new WeakMap();
_localBus = new WeakMap();
_crossTabBus = new WeakMap();
_requestResponseBus = new WeakMap();
_requestResponseHandlers = new WeakMap();
_active = new WeakMap();
_lastUpdatedAt = new WeakMap();
_alreadyTriggeredLocalEvents = new WeakMap();
_alreadyTriggeredCrossTabEvents = new WeakMap();
_onChannelMessage = new WeakSet();
onChannelMessage_fn = function(e) {
  const raw = e.data;
  let event;
  let args = [];
  let from;
  try {
    const msg = JSON.parse(raw);
    event = msg.event;
    args = msg.args;
    from = msg.from;
    if (event === "crossTabRequest") {
      const [requestId, method, payload, targets] = args;
      if (targets === "*" || targets.includes(__privateGet(this, _uuid))) {
        const handler = __privateGet(this, _requestResponseHandlers).get(method);
        const responsePromise = new Promise(async (resolve) => {
          let response;
          try {
            response = await handler?.(payload);
          } catch (error) {
            debug(`Error handling crossTabRequest "${method}"`, error);
          }
          resolve(response);
        });
        responsePromise.then((response) => {
          __privateGet(this, _channel).postMessage(
            JSON.stringify({
              event: "crossTabResponse",
              args: [requestId, response],
              from: __privateGet(this, _uuid)
            })
          );
        });
      } else {
        return;
      }
    }
    if (event === "crossTabResponse") {
      const [requestId, response] = args;
      __privateGet(this, _requestResponseBus).emit(requestId, response, from);
      return;
    }
    __privateGet(this, _crossTabBus).emit(event, ...args, from);
  } catch {
    return;
  }
};
_onWindowFocusChange = new WeakSet();
onWindowFocusChange_fn = function(active) {
  debug(`Tab is ${active ? "active" : "inactive"}`);
  __privateGet(this, _active).value = active;
  if (!active) {
    delete __privateGet(this, _alreadyTriggeredLocalEvents).value["tab:active"];
    this.emit("tab:inactive", { local: true });
    __privateGet(this, _lastUpdatedAt).value = Date.now();
  } else {
    __privateGet(this, _alreadyTriggeredLocalEvents).value["tab:inactive"];
    this.emit("tab:active", { local: true });
  }
  this.emit("tab:uuid", { local: false, crossTab: true }, __privateGet(this, _uuid), active);
};
_onWindowVisibilitychange = new WeakSet();
onWindowVisibilitychange_fn = function() {
  if (typeof document === "undefined") {
    return;
  }
  __privateMethod(this, _onWindowFocusChange, onWindowFocusChange_fn).call(this, document.visibilityState === "visible");
};
_awaitLocal = new WeakSet();
awaitLocal_fn = function(event, args) {
  const promises = [];
  if (!__privateGet(this, _localBus).e) {
    if (!["sw:fetch"].includes(event)) {
      debug(`No local bus listeners for "${event}"`);
    }
    return Promise.resolve();
  }
  if (__privateGet(this, _localBus).e[event]) {
    __privateGet(this, _localBus).e[event].forEach(({ fn }) => {
      promises.push(Promise.resolve(fn.apply(null, args)));
    });
  }
  if (promises.length) {
    return Promise.all(promises);
  }
  return Promise.resolve();
};
_awaitCrossTab = new WeakSet();
awaitCrossTab_fn = function(event, args) {
  return new Promise((resolve) => {
    this.crossTabRequest("awaitCrossTab", { event, args }).then(() => {
      resolve(void 0);
    });
  });
};
_addRequestHandler = new WeakSet();
addRequestHandler_fn = function(method, handler) {
  __privateGet(this, _requestResponseHandlers).set(method, handler);
};

const bus = {
  __proto__: null,
  BusService: BusService,
  shortid: shortid
};

export { BusService as B, bus as b, shortid as s };
//# sourceMappingURL=vueprint.1f7141fe.mjs.map
